import { tokenize } from './tokenize'
import type { DeviceInfo, FgConfig, FgEdit, FgNode, FgSection, ParseWarning } from './types'

/**
 * Reads model and firmware out of the `#config-version` header.
 *
 * The tokenizer drops comments, so this runs on the raw text before parsing. FortiOS
 * writes minor versions with two decimals — `5.04` means 5.4 — which is why the numeric
 * fields are derived rather than taken verbatim.
 */
export function parseDeviceInfo(text: string): DeviceInfo {
  const empty: DeviceInfo = {
    model: undefined, version: undefined, major: undefined, minor: undefined, build: undefined,
  }

  const header = /^#config-version=([A-Za-z0-9]+)-(\d+)\.(\d+)/m.exec(text)
  if (header === null) return empty

  const major = Number(header[2])
  // `04` is minor 4; `0` is minor 0. Strip the leading zero rather than reading it as 4.
  const minor = Number(header[3])

  return {
    model: header[1],
    version: `${header[2]}.${header[3]}`,
    major: Number.isFinite(major) ? major : undefined,
    minor: Number.isFinite(minor) ? minor : undefined,
    build: /-build(\d+)/.exec(text.slice(0, 200))?.[1],
  }
}

/**
 * Parses FortiOS `show full-configuration` (or `show`) output into a tree.
 *
 * Deliberately forgiving: real pastes are truncated mid-file, missing their trailing
 * `end`s, or copied out of a PDF. Structural problems become warnings and the parser
 * returns whatever it understood, because a partial audit beats an error page.
 */
export function parseFortigate(text: string): FgConfig {
  const warnings: ParseWarning[] = []
  const root: FgSection[] = []
  /** Innermost open block is last. Sections and edits interleave. */
  const stack: FgNode[] = []

  const current = (): FgNode | undefined => stack[stack.length - 1]

  const openSection = (path: string[], line: number): void => {
    const section: FgSection = { path, sets: {}, unsets: [], edits: [], sections: [], line }
    const parent = current()
    if (parent === undefined) root.push(section)
    else parent.sections.push(section)
    stack.push(section)
  }

  for (const { tokens, line } of tokenize(text)) {
    const head = tokens[0]
    if (head === undefined) continue
    const rest = tokens.slice(1)

    switch (head) {
      case 'config': {
        if (rest.length === 0) {
          warnings.push({ line, message: '`config` with no path was ignored' })
          break
        }
        openSection(rest, line)
        break
      }

      case 'edit': {
        const node = current()
        if (node === undefined || !isSection(node)) {
          warnings.push({ line, message: '`edit` outside a `config` block was ignored' })
          break
        }
        const entry: FgEdit = { key: rest.join(' '), sets: {}, unsets: [], sections: [], line }
        node.edits.push(entry)
        stack.push(entry)
        break
      }

      case 'next': {
        const node = current()
        if (node === undefined || isSection(node)) {
          warnings.push({ line, message: '`next` without a matching `edit` was ignored' })
          break
        }
        stack.pop()
        break
      }

      case 'end': {
        // Tolerate a missing `next`: an `end` while inside an edit closes both.
        const node = current()
        if (node !== undefined && !isSection(node)) stack.pop()
        if (stack.length === 0) {
          warnings.push({ line, message: '`end` without a matching `config` was ignored' })
          break
        }
        stack.pop()
        break
      }

      case 'set': {
        const node = current()
        const key = rest[0]
        if (node === undefined || key === undefined) {
          warnings.push({ line, message: '`set` outside a block, or with no option name, was ignored' })
          break
        }
        node.sets[key] = rest.slice(1)
        break
      }

      case 'unset': {
        const node = current()
        const key = rest[0]
        if (node === undefined || key === undefined) break
        node.unsets.push(key)
        delete node.sets[key]
        break
      }

      default:
        // `show`, `purge`, banner text and other noise. Ignored on purpose.
        break
    }
  }

  if (stack.length > 0) {
    warnings.push({
      line: stack[0]!.line,
      message: `Configuration ends with ${stack.length} unclosed block(s) — it may have been truncated when copied`,
    })
  }

  return { sections: root, warnings, device: parseDeviceInfo(text) }
}

function isSection(node: FgNode): node is FgSection {
  return 'path' in node
}

/** True when `text` looks like FortiOS config output, used to pick a parser automatically. */
export function looksLikeFortigate(text: string): boolean {
  return /^\s*(#config-version|config\s+system\s+global|config\s+firewall\s+policy)/m.test(text)
}

/**
 * True when this looks like a whole device configuration rather than an excerpt.
 *
 * Rules that conclude something from a section's ABSENCE — no NTP, no remote logging,
 * no password policy — are only sound on a complete export. People routinely paste just
 * the policy table, and telling them their clock is unsynchronised because they did not
 * include `config system ntp` is a false positive of exactly the kind that costs trust.
 *
 * `show full-configuration` always emits `config system global`, so its presence is the
 * cheapest reliable signal that the whole configuration is here.
 */
export function isFullConfiguration(config: FgConfig): boolean {
  return findSection(config, 'system', 'global') !== undefined
}

// ---------------------------------------------------------------------------
// Lookup helpers. Rules use these instead of walking the tree by hand, so that
// a rule reads as a statement about the configuration rather than as traversal.
// ---------------------------------------------------------------------------

/** Finds the first top-level section whose path matches exactly, e.g. `('system','global')`. */
export function findSection(config: FgConfig, ...path: string[]): FgSection | undefined {
  return findIn(config.sections, path)
}

function findIn(sections: FgSection[], path: string[]): FgSection | undefined {
  for (const section of sections) {
    if (pathEquals(section.path, path)) return section

    const nested = findIn(section.sections, path)
    if (nested !== undefined) return nested

    // Blocks nested inside an `edit` entry are common — SNMP hosts, policy app
    // categories, VPN proposals — so the search has to descend through edits too.
    for (const edit of section.edits) {
      const inEdit = findIn(edit.sections, path)
      if (inEdit !== undefined) return inEdit
    }
  }
  return undefined
}

/** All `edit` entries of the section at `path`, or `[]` when the section is absent. */
export function editsOf(config: FgConfig, ...path: string[]): FgEdit[] {
  return findSection(config, ...path)?.edits ?? []
}

/** The values of a `set` directive, or `[]` if unset. */
export function values(node: FgNode | undefined, key: string): string[] {
  return node?.sets[key] ?? []
}

/** The single value of a `set` directive, lowercased, or `undefined` if unset. */
export function value(node: FgNode | undefined, key: string): string | undefined {
  return node?.sets[key]?.[0]?.toLowerCase()
}

/** True when a `set` directive contains `needle`, compared case-insensitively. */
export function hasValue(node: FgNode | undefined, key: string, needle: string): boolean {
  const target = needle.toLowerCase()
  return values(node, key).some((v) => v.toLowerCase() === target)
}

function pathEquals(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((segment, i) => segment === b[i])
}
